/**
 * HorizontalStoryRow Component
 *
 * Netflix-style horizontal scrolling row of story cards.
 * Features arrow navigation on desktop and touch/swipe on mobile.
 */

import type { ReactElement } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { StoryVersion } from '@/types/story';
import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, BookOpen, Heart, Film, Library } from 'lucide-react';
import VersionBadge from '@/components/VersionBadge';
import ImagePreview from '@/components/ImagePreview';
import { getBookTypeLabel } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface StoryPage {
  page_number: number;
  image_url?: string;
}

export interface HorizontalStory {
  id: string;
  title: string;
  thumbnail?: string;
  age_range: string;
  version?: StoryVersion;
  is_pro_story?: boolean;
  is_favorited?: boolean;
  author_name?: string;
  user_id: string;
  pages?: StoryPage[];
  page_count?: number;
  favorites_count?: number;
  series_id?: string;
  series_title?: string;
  series_is_public?: boolean;
  has_animations?: boolean;
}

interface HorizontalStoryRowProps {
  title: string;
  icon: LucideIcon;
  iconColor: string;
  stories: HorizontalStory[];
  loading?: boolean;
  onSeeAll?: () => void;
  onStoryClick?: (storyId: string) => void;
}

// Helper to get full image URL
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const HorizontalStoryRow = ({
  title,
  icon: Icon,
  iconColor,
  stories,
  loading = false,
  onSeeAll,
  onStoryClick,
}: HorizontalStoryRowProps): ReactElement | null => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftArrow(scrollLeft > 10);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScrollPosition();
    window.addEventListener('resize', checkScrollPosition);
    return () => window.removeEventListener('resize', checkScrollPosition);
  }, [stories]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 400;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const handleStoryClick = (storyId: string) => {
    if (onStoryClick) {
      onStoryClick(storyId);
    } else {
      navigate(`/story/${storyId}`);
    }
  };

  // Don't render if no stories and not loading
  if (!loading && stories.length === 0) {
    return null;
  }

  return (
    <div className="relative group/row">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3 px-1">
        <h3
          className="font-bold text-base flex items-center gap-2"
          style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
        >
          <Icon className="w-5 h-5" style={{ color: iconColor }} />
          {title}
        </h3>
        {onSeeAll && (
          <button
            onClick={onSeeAll}
            className="text-xs font-semibold hover:underline flex items-center gap-1"
            style={{ fontFamily: 'Quicksand, sans-serif', color: iconColor }}
          >
            See all
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Scroll Container */}
      <div className="relative">
        {/* Left Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover/row:opacity-100 transition-all duration-200 hover:scale-110"
            style={{
              background: 'var(--st-bg-surface)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid var(--st-border-light)',
            }}
          >
            <ChevronLeft className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
          </button>
        )}

        {/* Right Arrow */}
        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover/row:opacity-100 transition-all duration-200 hover:scale-110"
            style={{
              background: 'var(--st-bg-surface)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid var(--st-border-light)',
            }}
          >
            <ChevronRight className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
          </button>
        )}

        {/* Stories Row */}
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto pb-2 horizontal-scroll-row"
          onScroll={checkScrollPosition}
        >
          {loading ? (
            // Loading Skeletons
            Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-[160px] h-[200px] rounded-xl animate-pulse"
                style={{ background: 'var(--st-border-light)' }}
              />
            ))
          ) : (
            stories.map((story) => (
              <div
                key={story.id}
                onClick={() => handleStoryClick(story.id)}
                className="flex-shrink-0 w-[160px] rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.03] hover:shadow-lg"
                style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
              >
                {/* Thumbnail with hover preview */}
                <div
                  className="h-[100px] relative overflow-hidden rounded-t-xl"
                  style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}
                >
                  {story.thumbnail ? (
                    <ImagePreview
                      thumbnail={story.thumbnail}
                      previewImages={story.pages?.map(page => page.image_url).filter(Boolean) as string[] || []}
                      alt={story.title}
                    >
                      {/* Badges */}
                      <div className="absolute top-1.5 left-1.5 flex items-center gap-1 z-10">
                        <VersionBadge version={story.version} isProStory={story.is_pro_story} size="small" />
                      </div>

                      {/* Series Icon */}
                      {story.series_title && story.series_is_public && (
                        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/90 flex items-center justify-center">
                          <Library className="w-2.5 h-2.5" style={{ color: '#4F46E5' }} />
                        </div>
                      )}

                      {/* Animated Icon */}
                      {story.has_animations && (
                        <div
                          className={`absolute top-1.5 ${story.series_title && story.series_is_public ? 'right-7' : 'right-1.5'} w-5 h-5 rounded-full bg-amber-500/90 flex items-center justify-center`}
                        >
                          <Film className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}

                      {/* Favorited */}
                      {story.is_favorited && (
                        <div
                          className={`absolute top-1.5 ${(story.series_title && story.series_is_public) || story.has_animations ? (story.series_title && story.series_is_public && story.has_animations ? 'right-[3.25rem]' : 'right-7') : 'right-1.5'} w-5 h-5 rounded-full bg-white/90 flex items-center justify-center`}
                        >
                          <Heart className="w-2.5 h-2.5 fill-current" style={{ color: 'var(--st-primary)' }} />
                        </div>
                      )}
                    </ImagePreview>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-8 h-8" style={{ color: 'var(--st-primary-hover)' }} />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-2">
                  {/* Age Badge */}
                  <span
                    className="inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold mb-1"
                    style={{ background: 'var(--st-border-light)', color: 'var(--st-text-heading)' }}
                  >
                    {getBookTypeLabel(story.age_range)}
                  </span>

                  {/* Title */}
                  <h4
                    className="font-bold text-xs line-clamp-2 leading-tight mb-1"
                    style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                  >
                    {story.title}
                  </h4>

                  {/* Author & Stats */}
                  <div className="flex items-center justify-between text-[9px]" style={{ color: 'var(--st-text-muted)' }}>
                    <span className="truncate max-w-[70px]" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                      {story.author_name || 'Anonymous'}
                    </span>
                    <span className="flex items-center gap-0.5">
                      <Heart className="w-2.5 h-2.5" />
                      {story.favorites_count || 0}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default HorizontalStoryRow;
